// The voice-leading ENGINES: one registry over the strands that grew up
// separately — the greedy chain, the global DP, and the Lab's parsimony
// mathematics — so the workbench can offer "how should the chords move?" as a
// first-class musical choice, the way it already offers the voicing family.
//
// An engine is a SEARCH × a METRIC. The two searches exist (greedy chain,
// Viterbi DP over the same candidate layers); the metrics are the guitarist's
// (fret movement — `voiceLeadingDistance`) and the composer's (pitch-space
// work — `pitchVoiceLeadingDistance`, where a held common tone is free). New
// engines are new rows here, not new plumbing.
//
// Form cycling is deliberately NOT parameterised: `optimizeFormCycle` depends
// on the greedy chain's Markov property (a pass's voicings are a function of
// its opening voicing only), so the cycle always rides the greedy engine
// regardless of this choice. The picker's hint says so out loud.

import {
  optimizeVoiceLeading,
  optimizeVoiceLeadingGlobal,
  pitchVoiceLeadingDistance,
  type OptimizableChord,
  type OptimizeOptions,
  type OptimizedChord,
} from './voiceLeading';

export const VOICE_LEADING_ENGINE_IDS = [
  'pathfinder-v1',
  'pathfinder-global-v1',
  'pathfinder-common-tones-v1',
] as const;

export type VoiceLeadingEngineId = (typeof VOICE_LEADING_ENGINE_IDS)[number];

export const DEFAULT_VOICE_LEADING_ENGINE_ID: VoiceLeadingEngineId = 'pathfinder-v1';

const LEGACY_ENGINE_IDS: Record<string, VoiceLeadingEngineId> = {
  greedy: 'pathfinder-v1',
  global: 'pathfinder-global-v1',
  parsimonious: 'pathfinder-common-tones-v1',
};

export interface VoiceLeadingEngine {
  id: VoiceLeadingEngineId;
  /** Stable product identity. Display copy may change; id + version may not. */
  name: 'Pathfinder';
  version: number;
  /** The musical promise made by this variant, not its implementation name. */
  variant: string;
  /** Picker label, including the public engine name and version. */
  label: string;
  /** One-line description for the picker's tooltip. */
  hint: string;
  run<T extends OptimizableChord>(
    sequence: T[],
    options?: OptimizeOptions,
  ): OptimizedChord<T>[] | null;
}

export const VOICE_LEADING_ENGINES: VoiceLeadingEngine[] = [
  {
    id: 'pathfinder-v1',
    name: 'Pathfinder',
    version: 1,
    variant: 'Nearest grip',
    label: 'Pathfinder v1 — Nearest grip',
    hint: 'Each chord takes the closest voicing to the one before — the classic engine, and the one form cycling always uses.',
    run: (sequence, options = {}) => optimizeVoiceLeading(sequence, options),
  },
  {
    id: 'pathfinder-global-v1',
    name: 'Pathfinder',
    version: 1,
    variant: 'Whole line',
    label: 'Pathfinder v1 — Whole line',
    hint: 'The smallest total hand movement across the entire form — never worse than Nearest grip, sometimes smoother.',
    run: (sequence, options = {}) => optimizeVoiceLeadingGlobal(sequence, options),
  },
  {
    id: 'pathfinder-common-tones-v1',
    name: 'Pathfinder',
    version: 1,
    variant: 'Common tones',
    label: 'Pathfinder v1 — Common tones',
    hint: 'Hold shared notes and move voices by steps — smoothness the EAR hears (pitch space), not the hand (frets). On a single string set this coincides with Whole line; turn Free string set ON to give the ear its freedom.',
    run: (sequence, options = {}) =>
      optimizeVoiceLeadingGlobal(sequence, {
        ...options,
        transitionCost: pitchVoiceLeadingDistance,
      }),
  },
];

const BY_ID = new Map(VOICE_LEADING_ENGINES.map((engine) => [engine.id, engine]));

/** Turn persisted legacy ids into the stable public contract. Unknown future
 * ids fail safely to Pathfinder v1 rather than leaving an unusable selection. */
export function normalizeVoiceLeadingEngineId(id: unknown): VoiceLeadingEngineId {
  if (typeof id !== 'string') return DEFAULT_VOICE_LEADING_ENGINE_ID;
  if (BY_ID.has(id as VoiceLeadingEngineId)) return id as VoiceLeadingEngineId;
  return LEGACY_ENGINE_IDS[id] ?? DEFAULT_VOICE_LEADING_ENGINE_ID;
}

export function voiceLeadingEngine(id: unknown): VoiceLeadingEngine {
  return BY_ID.get(normalizeVoiceLeadingEngineId(id))!;
}

export function isVoiceLeadingEngineId(value: unknown): value is VoiceLeadingEngineId {
  return typeof value === 'string' && BY_ID.has(value as VoiceLeadingEngineId);
}

export interface VoiceLeadingEngineDiagnostic {
  id: VoiceLeadingEngineId;
  name: string;
  version: number;
  variant: string;
  label: string;
  /** The unavailable value a future document requested, when fallback occurred. */
  requestedId?: string;
  fallback: boolean;
}

/** Serializable identity for exports, saved arrangements and diagnostics. */
export function describeVoiceLeadingEngine(id: unknown): VoiceLeadingEngineDiagnostic {
  const engine = voiceLeadingEngine(id);
  const requestedId = typeof id === 'string' ? id : undefined;
  const knownLegacy = requestedId !== undefined && LEGACY_ENGINE_IDS[requestedId] !== undefined;
  const fallback =
    requestedId !== undefined && !knownLegacy && !isVoiceLeadingEngineId(requestedId);
  return {
    id: engine.id,
    name: engine.name,
    version: engine.version,
    variant: engine.variant,
    label: engine.label,
    ...(fallback ? { requestedId } : {}),
    fallback,
  };
}
